/**
 * Alerts: open episodes grouped by the rule that found them, and the card
 * that shows one (2026-09-27).
 *
 * A rule firing on eleven ships is one alert with eleven ships in it, not
 * eleven rows. The card is design A1 of `docs/design/sketches/argus-alert-card-a.html`:
 * every region has a fixed place and height, ship chips are labels on a
 * 2 x 3 grid, and Acknowledge changes its words in place - never its size.
 * Acknowledgement is shared by the team (`argus-ack-views.html`): once
 * anyone acknowledges, the card goes still for everyone and says who.
 */

import { html, nothing, type TemplateResult } from 'lit';
import type { ArgusInboxItem } from '../../api/types.ts';
import { severityOf, when } from './argus-episodes.ts';

export interface ArgusAlert {
  /** The rule and the version that found its episodes. */
  key: string;
  ruleId: string;
  ruleName: string;
  sentence: string | null;
  severity: string;
  /** How many entities the rule watches, or null when unknown. */
  scope: number | null;
  noun: string;
  /** One per entity: under review first (not yet acknowledged before acknowledged), then decided; newest first within each. */
  items: ArgusInboxItem[];
  /** Under review and not acknowledged by anyone. */
  pending: ArgusInboxItem[];
  /** Given a verdict that closes it - it stays in the alert, marked, until every entity is decided. */
  decided: ArgusInboxItem[];
  /** Reached this person because they follow the rule: another team handles it (decision 35). */
  followed: boolean;
  /** May this person acknowledge, assign, mute and judge it - or only read it and add notes? */
  mayHandle: boolean;
  /** The rule's team. */
  teamName: string | null;
}

/** Closed by a verdict: anything but Needs more info. */
export function concluded(i: ArgusInboxItem): boolean {
  return i.verdict !== null && i.verdict !== 'needs_info';
}

const RANK: Record<string, number> = { critical: 0, watch: 1, info: 2 };

function ms(iso: string | null | undefined): number {
  return iso ? Date.parse(iso) : 0;
}

/** Group episodes into alerts: critical first, then the newest. */
export function groupAlerts(items: readonly ArgusInboxItem[]): ArgusAlert[] {
  const byKey = new Map<string, ArgusInboxItem[]>();
  for (const item of items) {
    const key = `${item.rule_id}:${item.version}`;
    const list = byKey.get(key);
    if (list) list.push(item);
    else byKey.set(key, [item]);
  }
  const alerts: ArgusAlert[] = [];
  for (const [key, list] of byKey) {
    const first = list[0]!;
    const sorted = [...list].sort(
      (a, b) =>
        Number(concluded(a)) - Number(concluded(b)) ||
        Number(a.mark === 'seen') - Number(b.mark === 'seen') ||
        ms(b.started_at) - ms(a.started_at),
    );
    alerts.push({
      key,
      ruleId: first.rule_id,
      ruleName: first.rule_name,
      sentence: first.sentence,
      severity: severityOf(first),
      // A scope smaller than what is firing means the source could not list
      // its entities just now: say nothing rather than "4/2".
      scope: ((): number | null => {
        const n = list.find((i) => i.scope_count != null)?.scope_count ?? null;
        return n != null && n >= list.length ? n : null;
      })(),
      noun: first.entity_noun ?? 'ship',
      items: sorted,
      pending: sorted.filter((i) => i.mark !== 'seen' && !concluded(i)),
      decided: sorted.filter(concluded),
      followed: !!first.followed_only,
      mayHandle: first.may_handle !== false,
      teamName: first.group_name ?? null,
    });
  }
  const newest = (a: ArgusAlert): number => Math.max(...a.items.map((i) => ms(i.started_at)));
  return alerts.sort((a, b) => (RANK[a.severity] ?? 1) - (RANK[b.severity] ?? 1) || newest(b) - newest(a));
}

/** How long, short enough for a chip: "40 m", "5 h 10 m", "2 d 3 h". */
export function shortSpan(fromIso: string, toIso: string | null): string {
  if (!toIso) return '-';
  const minutes = Math.max(0, Math.round((ms(toIso) - ms(fromIso)) / 60000));
  if (minutes < 60) return `${minutes} m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return minutes % 60 ? `${hours} h ${minutes % 60} m` : `${hours} h`;
  return hours % 24 ? `${Math.floor(hours / 24)} d ${hours % 24} h` : `${Math.floor(hours / 24)} d`;
}

/** "12 min ago", "3 h ago", "2 days ago". */
export function agoWords(iso: string | null, now = Date.now()): string {
  if (!iso) return '';
  const minutes = Math.max(0, Math.round((now - ms(iso)) / 60000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
}

function acknowledgers(alert: ArgusAlert): string[] {
  return [
    ...new Set(alert.items.filter((i) => i.mark === 'seen' && !concluded(i)).map((i) => i.marked_by_name ?? 'Someone')),
  ];
}

const VERDICT_WORD: Record<string, string> = {
  confirmed: 'Confirmed',
  not_an_issue: 'Not an issue',
  duplicate: 'Duplicate',
};

export interface CardActions {
  /** Acknowledge what nobody has yet. Absent when this person may not. */
  acknowledge?: ((alert: ArgusAlert) => void) | undefined;
  review: (alert: ArgusAlert) => void;
  menu: (alert: ArgusAlert, x: number, y: number) => void;
  busy?: boolean;
}

const SLOTS = 6;

function chip(alert: ArgusAlert, item: ArgusInboxItem): TemplateResult {
  if (concluded(item)) {
    const word = VERDICT_WORD[item.verdict ?? ''] ?? 'Closed';
    return html`<div
      class="argus-ac-chip is-decided is-v-${item.verdict}"
      title=${`${item.entity_name}: ${word}${item.judged_by_name ? ` by ${item.judged_by_name}` : ''}`}
    >
      <span class="argus-ac-chip-n">${item.entity_name}</span>
      <span class="argus-ac-chip-v">${word}</span>
    </div>`;
  }
  const acked = item.mark === 'seen';
  // New: nobody acknowledged it, but someone acknowledged the rest - it
  // started after they looked, and it is why the card breathes again.
  const review = alert.items.length - alert.decided.length;
  const fresh = !acked && alert.pending.length < review;
  return html`<div
    class="argus-ac-chip ${fresh ? 'is-new' : ''}"
    title=${`${item.entity_name}: since ${when(item.started_at)}${acked ? ` · acknowledged by ${item.marked_by_name ?? 'someone'}` : fresh ? ' · new since the last acknowledgement' : ''}`}
  >
    ${acked ? html`<span class="argus-ac-tick" aria-label="acknowledged">✓</span>` : nothing}
    <span class="argus-ac-chip-n">${item.entity_name}</span>
    <span class="argus-ac-chip-d">${shortSpan(item.started_at, item.ended_at ?? item.last_at)}</span>
  </div>`;
}

function grid(alert: ArgusAlert): TemplateResult {
  const list = alert.items;
  const over = list.length > SLOTS;
  const shown = over ? list.slice(0, SLOTS - 1) : list;
  const cells: TemplateResult[] = shown.map((i) => chip(alert, i));
  if (over) cells.push(html`<div class="argus-ac-chip is-more">+ ${list.length - shown.length} more</div>`);
  while (cells.length < SLOTS) cells.push(html`<div class="argus-ac-chip is-empty" aria-hidden="true"></div>`);
  return html`<div class="argus-ac-grid">${cells}</div>`;
}

function slot(alert: ArgusAlert, actions: CardActions): TemplateResult {
  const pending = alert.pending.length;
  if (!pending) {
    const who = acknowledgers(alert);
    const latest = alert.items.reduce<string | null>(
      (at, i) => (i.marked_at && (!at || ms(i.marked_at) > ms(at)) ? i.marked_at : at),
      null,
    );
    return html`<div class="argus-ac-act is-by" title="Undo acknowledge is in the right-click menu and on the alert's page">
      <span class="argus-ac-tick">✓</span><span class="argus-ac-by">${who.join(', ')}</span>
      <span class="argus-ac-when">${agoWords(latest)}</span>
    </div>`;
  }
  if (!alert.mayHandle) {
    return html`<div class="argus-ac-act is-by is-none" title="You follow this rule: you read its alerts and add notes">
      ${alert.teamName ?? 'Its team'} handles it
    </div>`;
  }
  if (!actions.acknowledge) return html`<div class="argus-ac-act is-by is-none">Not acknowledged yet</div>`;
  const some = pending < alert.items.length - alert.decided.length;
  const label = some ? `Acknowledge ${pending} new` : pending > 1 ? `Acknowledge all ${pending}` : 'Acknowledge';
  const ack = actions.acknowledge;
  return html`<button
    type="button"
    class="argus-ac-act is-ack"
    ?disabled=${actions.busy}
    title="Say the team has seen it: the glow stops for everyone, and it stays open until it gets a verdict or ends"
    @click=${() => ack(alert)}
  >
    ${label}
  </button>`;
}

/** One line under the grid: what is decided, and who acknowledged what. Empty, but always there, when there is nothing to say. */
function ackLine(alert: ArgusAlert): TemplateResult {
  const review = alert.items.length - alert.decided.length;
  const acked = review - alert.pending.length;
  const parts: TemplateResult[] = [];
  if (alert.decided.length) parts.push(html`<b>${alert.decided.length} closed</b>, ${review} under review`);
  if (acked && alert.pending.length) {
    parts.push(html`${acked} acknowledged by <b>${acknowledgers(alert).join(' and ')}</b> · <b>${alert.pending.length} new</b>`);
  }
  if (!parts.length) return html`&nbsp;`;
  return html`${parts.map((p, i) => html`${i ? ' · ' : ''}${p}`)}`;
}

/**
 * Who has the alert's ships. Assignment is per ship, so the card says how many
 * are assigned whenever that is not all of them - "Guja assigned" over three
 * ships when Guja took one was a false claim.
 */
export function ownerSummary(alert: ArgusAlert): { names: string[]; assigned: number; total: number } {
  const names: string[] = [];
  let assigned = 0;
  for (const i of alert.items) {
    if (!i.owner_name) continue;
    assigned += 1;
    if (!names.includes(i.owner_name)) names.push(i.owner_name);
  }
  return { names, assigned, total: alert.items.length };
}

function ownerLine(alert: ArgusAlert): TemplateResult | string {
  const { names, assigned, total } = ownerSummary(alert);
  if (!assigned) return 'unassigned';
  const who = names.map((n, i) => html`${i ? ', ' : ''}<b>${n}</b>`);
  if (assigned === total) return html`${who} assigned`;
  return html`${who} assigned ${assigned} of ${total} ${alert.noun}s`;
}

/** One alert as card A1, in its glow: running edge for critical and watch, aurora for info, still once acknowledged. */
export function renderAlertCard(alert: ArgusAlert, actions: CardActions): TemplateResult {
  const sev = alert.severity;
  const calm = alert.pending.length === 0;
  const since = alert.items.reduce((a, i) => (ms(i.started_at) < ms(a) ? i.started_at : a), alert.items[0]!.started_at);
  const n = alert.items.length;
  const body = html`<div class="argus-ac">
    <div class="argus-ac-top">
      <span class="argus-ac-lead">
        <span class="glow-dot is-${sev}" aria-hidden="true"></span>
        <span class="argus-chip is-${sev}">${sev}</span>
      </span>
      <span class="argus-ac-name" title=${alert.ruleName}>${alert.ruleName}</span>
      ${alert.followed
        ? html`<span class="argus-chip is-followed" title=${`You follow this rule. ${alert.teamName ?? 'Its team'} handles its alerts.`}>followed</span>`
        : nothing}
      <span class="argus-ac-count" title=${`Firing on ${n} of the ${alert.scope ?? '?'} ${alert.noun}s this rule watches`}>
        <b>${n}${alert.scope != null ? html`<small>/${alert.scope}</small>` : nothing}</b>
        <span>${(alert.scope ?? n) === 1 ? alert.noun : `${alert.noun}s`}</span>
      </span>
    </div>
    <div class="argus-ac-sentence" title=${alert.sentence ?? ''}>${alert.sentence ?? ''}</div>
    ${grid(alert)}
    <div class="argus-ac-meta">
      <span>Since ${when(since)}</span>
      <span class="argus-ac-owner">· ${ownerLine(alert)}</span>
    </div>
    <div class="argus-ac-line">${ackLine(alert)}</div>
    <div class="argus-ac-actions">
      ${slot(alert, actions)}
      <button type="button" class="argus-ac-act is-review" @click=${() => actions.review(alert)}>Review →</button>
    </div>
  </div>`;
  const onMenu = (e: MouseEvent): void => {
    e.preventDefault();
    actions.menu(alert, e.clientX, e.clientY);
  };
  if (calm) {
    return html`<article class="argus-alert is-calm is-${sev}" aria-label=${`${alert.ruleName}, acknowledged`} @contextmenu=${onMenu}>${body}</article>`;
  }
  if (sev === 'info') {
    return html`<article class="argus-alert glow-card is-${sev}" aria-label=${alert.ruleName} @contextmenu=${onMenu}>
      <div class="glow-card-in">${body}</div>
    </article>`;
  }
  return html`<article class="argus-alert glow-edge is-${sev}" aria-label=${alert.ruleName} @contextmenu=${onMenu}>
    <span class="glow-edge-ring" aria-hidden="true"></span>
    <div class="glow-edge-in">${body}</div>
  </article>`;
}
